import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Table, Column } from '../../ui/table';
import { Search } from '../../ui/search';
import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';

import { Loader, NoResult } from '../../misc/loader-utils';
import Graph from '../../base/megatable/graph';
import { getTicks, processEditGraphStats } from '../../base/megatable/graph-utils';
import Pagination from '../../base/common/pagination';
import { dataMap, getChartOptions, isEvergreen } from '../campaign-tracking/utils';

import GranularitiesDropdown from '../../base/dropdowns/granularities-dropdown';
import { PublisherDropdown } from '../campaign-tracking/multiselect-dropdowns';
import DatePicker from '../../base/date/date-picker';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import { usePublisherList } from '../../../utils/custom-hooks';
import graphIcon from '../../../images/icons/graph.svg';
import { buildQueryFilter } from '../../../utils/utils';
import Button from '../../ui/button';

const TopStrategyTab = props => {
  const {
    dateRange,
    dateFilterRange,
    setDateFilterRange,
    granularities,
    setGranularities,
    title,
    name,
    endpoints,
    filterByPublisher,
    columns,
    displayNameField,
    graphParamKey,
    rowKeyField,
    reportType,
    linkedEntityInfo,
    stickyColumnsCount,
  } = props;

  const { strategyId } = useParams();
  const queryParams = new URLSearchParams(useLocation().search);
  const linkedFieldValue = linkedEntityInfo ? queryParams.get(linkedEntityInfo.paramField) : null;
  const chartOptions = getChartOptions(name);
  const history = useHistory();

  //	Filters States
  //	TODO: Refactor dropdowns to simplify selected state
  const granularity =
    granularities.length > 0 && granularities.filter(item => item.selected)[0].name;
  const [pubOptions, setPubOptions] = usePublisherList(session.orgId, true);
  const selectedPubs = pubOptions.filter(item => item.selected);
  const publishers = pubOptions.reduce((agg, item) => {
    if (item.selected) agg += `${agg ? ',' : ''}${item.name}`;
    return agg;
  }, '');

  const numPubsSelected = pubOptions.filter(item => item.selected).length;
  const tabColumns = dataMap[name].columns;
  const [dropdownColumns, setDropdownColumns] = useState(tabColumns);
  const selectedColumns = new Set(dropdownColumns.filter(col => col.selected).map(col => col.name));
  const [searchQuery, setSearchQuery] = useState('');
  const [linkedFilter, setLinkedFilter] = useState(null);

  //	Graph State
  const [showGraph, setGraphVisibility] = useState(true);
  const [chartOption, setChartOption] = useState(chartOptions[0]);
  const [graphData, setGraphData] = useState([]);
  const [ticks, setTicks] = useState([]);
  const [graphIsLoading, setGraphIsLoading] = useState(false);

  //	Table State
  const [totalsData, setTotalsData] = useState({});
  const [tableData, setTableData] = useState({});
  const [selectedRows, setSelectedRows] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [tableIsLoading, setTableIsLoading] = useState(false);

  //	Graph & Table Shared State
  const [selectedColors, setSelectedColors] = useState({});

  const graphColors = ['green', 'purple', 'red', 'blue'];

  //	Breadcrumb Data
  useEffect(() => {
    if (!linkedEntityInfo) return;

    const { paramField, displayField, name } = linkedEntityInfo;

    if (paramField && linkedFieldValue) {
      const entityParams = {};
      if (name === 'products' && queryParams.get('product_ids')) {
        entityParams.product_ids = queryParams.get('product_ids');
      } else {
        entityParams[paramField] = linkedFieldValue;
      }

      api.get(endpoints.breadcrumb, entityParams).then(resp => {
        const data = resp.data.data[0];

        setLinkedFilter({
          [paramField]: data[name][0][displayField],
        });
      });
    }
  }, []);

  //	Table Data
  useEffect(() => {
    if (!dateFilterRange || pubOptions.length === 0) return;

    if (!selectedRows) {
      ReactDOM.unstable_batchedUpdates(() => {
        setGraphIsLoading(true);
        setTableIsLoading(true);
      });
    }

    queryTable().then(res => {
      if (!selectedRows || graphData.length === 0) {
        const initialGraphedRows = res.stats.slice(0, 2);
        onSelectRows(initialGraphedRows);
      }
    });
  }, [page, perPage, publishers, dateFilterRange, linkedFieldValue, searchQuery]);

  useEffect(() => {
    queryGraph();
  }, [selectedRows, granularity, dateFilterRange]);

  //	Data Fetchers

  const queryGraph = () => {
    if (!selectedRows) {
      return;
    }
    setGraphIsLoading(true);

    const selectedArray = selectedRows ? Object.values(selectedRows).filter(row => !!row) : [];
    const requests = selectedArray.map(row => {
      const rowParams = {
        date_from: dateFilterRange.date_from,
        date_to: dateFilterRange.date_to,
        granularity: granularity === 'monthly' ? 'monthly' : 'daily',
        [graphParamKey]: row[rowKeyField],
      };

      return api.get(endpoints.graph, rowParams);
    });

    Promise.all(requests).then((respArr, i) => {
      const respData = respArr
        .filter(resp => resp.data.data[0].stats.length !== 0)
        .map((resp, i) => {
          const respStats = resp.data.data[0].stats;
          return {
            data: respStats,
            name: selectedArray[i][displayNameField],
            color: selectedColors[selectedArray[i][rowKeyField]],
          };
        });

      const ticks = getTicks(granularity, dateFilterRange.date_from, dateFilterRange.date_to);
      const graphStats = processEditGraphStats(respData, ticks, granularity, dateFilterRange);

      ReactDOM.unstable_batchedUpdates(() => {
        setGraphData(graphStats);
        setTicks(ticks);
        setGraphIsLoading(false);
      });
    });
  };

  const queryTable = () => {
    setTableIsLoading(true);
    const requests = [
      api.get(endpoints.aggregate, getTableParams(true)),
      api.get(endpoints.table, getTableParams()),
    ];

    return Promise.all(requests).then((respArr, i) => {
      const totalsResp = respArr[0].data.data[0];
      const tablesResp = respArr[1].data.data[0];

      setTotalsData(totalsResp);
      setTableData(tablesResp);
      setTableIsLoading(false);
      checkGraphedRows(tablesResp.stats);

      return tablesResp;
    });
  };

  //	Event Handlers
  const onSelectRows = rows => {
    if (rows.length === 0) return;
    const colors = {};
    const newSelectedRows = selectedRows ? { ...selectedRows } : {};

    rows.forEach(row => {
      const rowKey = row[rowKeyField];
      if (!rowKey) return;

      if (newSelectedRows[rowKey]) {
        delete newSelectedRows[rowKey];
      } else {
        newSelectedRows[rowKey] = row;
      }
    });

    Object.values(newSelectedRows)
      .filter(r => !!r)
      .forEach((row, idx) => {
        const rowKey = row[rowKeyField];
        colors[rowKey] = graphColors[idx % graphColors.length];
      });

    ReactDOM.unstable_batchedUpdates(() => {
      setSelectedColors(colors);
      setSelectedRows(newSelectedRows);
    });
  };

  //	Helper Functions

  const getTableParams = (aggregate, report) => {
    const params = {
      date_from: dateFilterRange.date_from,
      date_to: dateFilterRange.date_to,
      strategy_id: parseInt(strategyId),
    };

    if (publishers && filterByPublisher) {
      params.pub_ids = publishers;
    }

    if (!aggregate && !report) {
      params.page = page;
      params.per_page = perPage;
    }

    if (linkedFieldValue && linkedEntityInfo) {
      params[linkedEntityInfo.paramField] = linkedFieldValue;
    }

    if (searchQuery) {
      params.filters = buildQueryFilter([
        { field: displayNameField, operation: 'contains', value: searchQuery },
      ]);
    }

    if (report) {
      params.reportParams = {
        get_totals: true,
      };
    }

    return params;
  };

  const checkGraphedRows = tableData => {
    if (!selectedRows) return;
    const rowsToUnselect = [];

    Object.entries(selectedRows).forEach(([graphRowKey, graphRow]) => {
      if (
        !tableData.some(tableRow => tableRow[rowKeyField].toString() === graphRowKey.toString())
      ) {
        rowsToUnselect.push(graphRow);
      }
    });

    onSelectRows(rowsToUnselect);
  };

  const removeLinkedFilter = () => {
    queryParams.delete(linkedEntityInfo.paramField);
    history.replace({
      search: queryParams.toString(),
    });
  };

  //	Render Functions

  const TabHeader = () => {
    return (
      <div className="strategy-tab-header">
        <div className="title">{`Top ${title}`}</div>
        {linkedFieldValue && linkedFilter && linkedFilter[linkedEntityInfo.paramField] && (
          <div className="linked-filter">
            for "{linkedFilter[linkedEntityInfo.paramField]}"
            <span className="linked-cancel-btn">
              <Button onClick={removeLinkedFilter} kind="Text" size={14} iconOnly="circleX" />
            </span>
          </div>
        )}
      </div>
    );
  };

  const Filters = ({ isShowing }) => {
    if (!isShowing) return null;

    const pubLabel =
      selectedPubs.length === 0
        ? 'Publisher'
        : selectedPubs.length > 1
        ? `${selectedPubs.length} Publishers`
        : selectedPubs[0].label;

    const strategyIsEvergreen = isEvergreen(strategyId);

    return (
      <div className="filters">
        <ChartOptions options={chartOptions} isShowing={showGraph} />
        <div className="dropdowns">
          {granularities.length > 0 && (
            <GranularitiesDropdown
              items={granularities}
              updateGranularities={setGranularities}
              showGraph={true}
              gaEvent={{
                category: 'Strategy Performance Tabs',
                label: `Publishers Granularity`,
              }}
            />
          )}
          {filterByPublisher && (
            <PublisherDropdown
              gaEvent={{
                category: `Strategy Tab ${title}`,
                label: 'Publisher Dropdown',
              }}
              items={pubOptions}
              updateOptions={setPubOptions}
              staticFields={[]}
              allSelected={numPubsSelected === pubOptions.length}
              numSelect={numPubsSelected}
              label={pubLabel}
              disabled={linkedFieldValue}
            />
          )}
          <DatePicker
            eventPrefix={`Strategy Performance ${title} Date Picker`}
            setDateRange={setDateFilterRange}
            dateRange={{
              startDate: dateService.dateFromStr(dateFilterRange.date_from),
              endDate: dateService.dateFromStr(dateFilterRange.date_to),
            }}
            allowFutureDates={false}
            futureLimit={dateRange.date_to}
            disablePastDates={!strategyIsEvergreen && !!dateRange.date_from}
            pastDateThreshold={strategyIsEvergreen ? null : dateRange.date_from}
          />
          <ColumnsDropdown
            gaEvent={{
              category: 'Strategy Performance Tabs',
              label: `${title} Column`,
            }}
            items={dropdownColumns}
            updateColumns={columns => setDropdownColumns(columns)}
            resetColumns={() => setDropdownColumns(tabColumns)}
            staticFields={['pub_name']}
          />
          {/* TODO: Refactor with Button Component */}
          <div
            className={`graph-btn ${showGraph ? 'active' : ''}`}
            onClick={() => setGraphVisibility(!showGraph)}
          >
            <img src={graphIcon} alt="graph icon" />
          </div>
          <ReportDropdown
            statType={reportType}
            orgId={session.orgId}
            gaEvent={{
              category: `Strategy Tab ${title}`,
              label: 'Export Data',
            }}
            queryParams={getTableParams(false, true)}
            columns={dropdownColumns}
            currentPage={page}
            perPage={perPage}
            rowCounts={{
              visible: tableData.stats && tableData.stats.length,
              all: tableData.page_info && tableData.page_info.total_items,
            }}
            disabled={tableIsLoading}
            reportVersion="V2"
          />
        </div>
      </div>
    );
  };

  const ChartOptions = ({ options, isShowing }) => {
    if (!isShowing) return <div />;
    return (
      <div className="chart-options">
        {options.map((item, i) => {
          return (
            <div
              key={`chart-option-${i}`}
              className={`option ${chartOption.statKey === item.statKey ? 'selected' : ''}`}
              onClick={() => setChartOption(item)}
            >
              {item.label}
            </div>
          );
        })}
      </div>
    );
  };

  const StrategyStatsSearch = ({ isShowing }) => {
    if (!isShowing) return null;

    return (
      <div className="strategy-stats-search">
        <Search debounce={true} onSearch={setSearchQuery} placeholder={`Search ${name}...`} />
        {tableData.stats && (
          <div className="result-count">
            Displaying {tableData.stats.length} {title}
          </div>
        )}
      </div>
    );
  };

  const StrategyGraph = ({ loading, data, isShowing }) => {
    if (!isShowing) return null;

    let content;
    if (loading) {
      content = <Loader loading={true} />;
    } else if (data.length === 0) {
      content = <NoResult noResult={true} type="strategy-performance" />;
    } else {
      content = (
        <Graph
          stats={data}
          xTicks={ticks}
          dates={dateFilterRange}
          chartOptions={chartOption}
          granularity={granularity}
        />
      );
    }

    return <div className="megatable-graph">{content}</div>;
  };

  const StrategyTable = ({ loading, data }) => {
    let content;
    if (loading) {
      content = <Loader loading={true} />;
    } else if (!data.stats || data.stats.length === 0) {
      content = <NoResult noResult={true} type="strategy-performance" />;
    } else if (selectedColumns.size === 0) {
      content = <NoResult noResult={true} type="no-columns" />;
    } else {
      content = (
        <>
          <Table
            data={data.stats}
            totals={totalsData.stats}
            rowIdField={rowKeyField}
            visibleColumns={selectedColumns}
            selectLimit={2}
            selectedColors={selectedColors}
            selectedRows={selectedRows}
            selectable={true}
            stickyColumnsCount={stickyColumnsCount}
            onRowSelect={({ rowData }) => onSelectRows([rowData])}
          >
            {columns.map((col, idx) => (
              <Column
                key={col.name}
                dataKey={col.name}
                label={col.label}
                justifyContent={col.justifyContent}
                width={col.width}
                cellRender={col.cellRender}
                format={col.format}
                flexWidth={col.flexWidth}
                maxWidth={col.maxWidth}
                tooltip={col.tooltip}
              />
            ))}
          </Table>
          <Pagination
            showPagination={true}
            currentPage={page}
            totalItems={data.page_info.total_items}
            perPage={perPage}
            updatePage={val => setPage(val)}
            updatePerPage={val => {
              setPerPage(val);
              setPage(1);
            }}
            reducedPagination={true}
          />
        </>
      );
    }

    return <div className="strategy-table">{content}</div>;
  };

  return (
    <div className="strategy-performance-stats">
      {TabHeader()}
      {Filters({ isShowing: !!dateFilterRange })}
      {StrategyGraph({ data: graphData, loading: graphIsLoading, isShowing: showGraph })}
      {StrategyStatsSearch({ isShowing: !!tableData.stats })}
      {StrategyTable({ data: tableData, loading: tableIsLoading })}
    </div>
  );
};

TopStrategyTab.propTypes = {
  graphParamKey: PropTypes.string.isRequired,
  displayNameField: PropTypes.string.isRequired,
  rowKeyField: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  endpoints: PropTypes.shape({
    aggregate: PropTypes.string,
    graph: PropTypes.string,
    table: PropTypes.string,
  }).isRequired,
  dateRange: PropTypes.shape({ date_from: PropTypes.object, date_to: PropTypes.object }),
  dateFilterRange: PropTypes.shape({ date_from: PropTypes.string, date_to: PropTypes.string }),
  title: PropTypes.string,
  setDateFilterRange: PropTypes.func,
  granularities: PropTypes.arrayOf(PropTypes.shape),
  setGranularities: PropTypes.func,
  filterByPublisher: PropTypes.bool,
  stickyColumnsCount: PropTypes.number,
  linkedEntityInfo: PropTypes.shape({
    name: PropTypes.string,
    paramField: PropTypes.string,
    displayField: PropTypes.string,
  }),
};

TopStrategyTab.defaultProps = {
  dateRange: null,
  dateFilterRange: null,
  title: '',
  setDateFilterRange: () => {},
  granularities: [],
  setGranularities: () => {},
  filterByPublisher: false,
  stickyColumnsCount: 0,
  linkedEntityInfo: null,
};

export default TopStrategyTab;
